/**
 * exception-telemetry
 *
 * Privacy-safe exception payload helpers shared by client and server capture.
 *
 * Primary responsibilities:
 * - Normalize unknown thrown values into non-empty exception fields
 * - Redact sensitive values before telemetry dispatch
 * - Build a stable PostHog/Sentry context payload
 *
 * Dependencies:
 * - Browser location and storage APIs when available
 */

export type ExceptionSeverity = 'fatal' | 'error' | 'warning' | 'info'

export type ActivationSurface =
  | 'public_chat'
  | 'shared_chat'
  | 'invite_register'
  | 'chat'
  | 'spaces'
  | 'nexus'
  | 'unknown'

export interface ExceptionTelemetryContext {
  source: string
  boundary?: string | null
  path?: string | null
  route?: string | null
  userId?: string | null
  anonymousId?: string | null
  sessionId?: string | null
  release?: string | null
  commitSha?: string | null
  severity?: ExceptionSeverity
  firstSession?: boolean | null
  activationSurface?: ActivationSurface | null
  activationStage?: string | null
  digest?: string | null
  safeQueryKeys?: string[] | null
}

export interface NormalizedException {
  name: string
  message: string
  digest?: string
  stack?: string
}

export type ExceptionTelemetryPayload = Record<string, unknown> & {
  '$exception_type': string
  '$exception_message': string
  '$exception_level': ExceptionSeverity
  '$exception_stack_trace_raw'?: string
  exception_type: string
  exception_name: string
  exception_message: string
  exception_stack?: string
  error_type: string
  error_name: string
  error_message: string
  error_stack?: string
  stack?: string
  source: string
  severity: ExceptionSeverity
  path: string
  route: string
  activation_surface: ActivationSurface
  dedupe_key: string
  boundary?: string
  digest?: string
  user_id?: string
  anonymous_id?: string
  session_id?: string
  release?: string
  commit_sha?: string
  first_session?: boolean
  activation_stage?: string
  query_keys?: string[]
}

type BrowserTelemetryContext = Pick<
  ExceptionTelemetryContext,
  'path' | 'route' | 'safeQueryKeys'
>

const DEFAULT_EXCEPTION_NAME = 'Error'
const DEFAULT_EXCEPTION_MESSAGE = 'Unknown client exception'
const MAX_MESSAGE_LENGTH = 240
const MAX_STACK_LENGTH = 4000
const MAX_LABEL_LENGTH = 96

const sensitiveQueryKeys = new Set([
  'api_key',
  'apikey',
  'authorization',
  'body',
  'code',
  'content',
  'email',
  'invite',
  'invite_code',
  'key',
  'message',
  'password',
  'prompt',
  'q',
  'query',
  'secret',
  'token'
])

const sensitiveKeyPattern =
  /\b(api[_-]?key|apikey|authorization|bearer|token|secret|password|invite[_-]?code|invite|code|email|prompt|message|content|body|query|input)\b\s*[:=]\s*("[^"]*"|'[^']*'|[^\s&,;]+)/gi
const privateContentPattern =
  /\b(prompt|message|content|body|query|input)\b\s*[:=]\s*.+$/gi
const emailPattern = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi
const bearerPattern = /\b(Bearer\s+)[A-Za-z0-9._~+/=-]+/gi
const jwtPattern = /\b[A-Za-z0-9_-]{16,}\.[A-Za-z0-9_-]{16,}\.[A-Za-z0-9_-]{10,}\b/g
const apiKeyPattern = /\b(sk|pk|ghp|gho|ghu|ghs|xoxb|xoxp)[-_][A-Za-z0-9_-]{12,}\b/g
const urlPattern = /https?:\/\/[^\s)]+/gi

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function readString(value: unknown) {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function readRecordString(record: Record<string, unknown>, key: string) {
  return readString(record[key])
}

function truncate(value: string, maxLength: number) {
  if (value.length <= maxLength) return value
  return `${value.slice(0, maxLength - 3)}...`
}

function sanitizeTelemetryLabel(value: unknown, fallback: string) {
  const raw = readString(value) ?? fallback
  const normalized = raw
    .replace(/[^A-Za-z0-9_.:/-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')

  return truncate(normalized || fallback, MAX_LABEL_LENGTH)
}

function redactSensitiveText(value: string, options?: { redactUrls?: boolean }) {
  const shouldRedactUrls = options?.redactUrls ?? true

  let redacted = value
    .replace(emailPattern, '[redacted-email]')
    .replace(bearerPattern, '$1[redacted]')
    .replace(jwtPattern, '[redacted-token]')
    .replace(apiKeyPattern, '$1-[redacted]')
    .replace(privateContentPattern, '$1=[redacted]')
    .replace(sensitiveKeyPattern, '$1=[redacted]')

  if (shouldRedactUrls) {
    redacted = redacted.replace(urlPattern, '[redacted-url]')
  }

  return redacted
}

export function sanitizeExceptionMessage(
  value: unknown,
  fallback = DEFAULT_EXCEPTION_MESSAGE
) {
  const raw = readString(value) ?? fallback
  const firstLine = raw.split(/\r?\n/)[0] ?? fallback
  const normalized = firstLine.replace(/\s+/g, ' ').trim()
  const redacted = redactSensitiveText(normalized)
  const sanitized = truncate(redacted, MAX_MESSAGE_LENGTH).trim()

  return sanitized || fallback
}

export function sanitizeExceptionName(value: unknown) {
  return sanitizeTelemetryLabel(value, DEFAULT_EXCEPTION_NAME)
}

function readDigest(error: unknown) {
  if (!isRecord(error)) return null

  return readRecordString(error, 'digest')
}

export function sanitizePath(value: string | null | undefined, fallback = '/') {
  const raw = readString(value) ?? fallback

  try {
    const url = new URL(raw, 'https://losi.local')
    return url.pathname || fallback
  } catch {
    const [withoutQuery] = raw.split(/[?#]/)
    return withoutQuery || fallback
  }
}

export function getSafeQueryKeyNames(search: string | null | undefined) {
  if (!search) return undefined

  const queryStart = search.indexOf('?')
  const rawSearch =
    queryStart >= 0 ? search.slice(queryStart) : search.startsWith('?') ? search : `?${search}`
  const [normalizedSearch] = rawSearch.split('#')
  const params = new URLSearchParams(normalizedSearch)
  const keys = Array.from(new Set(Array.from(params.keys())))
    .map((key) => key.trim())
    .filter(Boolean)
    .filter((key) => !sensitiveQueryKeys.has(key.toLowerCase()))
    .sort()

  return keys.length > 0 ? keys : undefined
}

export function getBrowserExceptionTelemetryContext(): BrowserTelemetryContext {
  if (typeof window === 'undefined') {
    return {
      path: '/',
      route: '/'
    }
  }

  return {
    path: window.location.pathname,
    route: window.location.pathname,
    safeQueryKeys: getSafeQueryKeyNames(window.location.search)
  }
}

export function inferActivationSurface(
  path: string,
  queryKeys?: string[] | null
): ActivationSurface {
  const safePath = sanitizePath(path)
  const normalizedQueryKeys = new Set(
    (queryKeys ?? []).map((key) => key.toLowerCase())
  )

  if (
    safePath.startsWith('/register') ||
    safePath.startsWith('/signup') ||
    safePath.includes('/invite') ||
    normalizedQueryKeys.has('register')
  ) {
    return 'invite_register'
  }

  if (
    safePath.startsWith('/try') ||
    safePath.startsWith('/agent/') ||
    safePath.startsWith('/embed/nexus')
  ) {
    return 'public_chat'
  }

  if (safePath.startsWith('/chat/share') || safePath.startsWith('/message/share')) {
    return 'shared_chat'
  }

  if (safePath.startsWith('/spaces')) {
    return 'spaces'
  }

  if (safePath.startsWith('/nexus')) {
    return 'nexus'
  }

  if (safePath.startsWith('/chat')) {
    return 'chat'
  }

  return 'unknown'
}

function sanitizeStackUrl(value: string) {
  try {
    const url = new URL(value)
    return `${url.origin}${url.pathname}`
  } catch {
    return '[redacted-url]'
  }
}

export function sanitizeErrorStack(
  stack: string | undefined,
  name: string,
  message: string
) {
  if (!stack) return undefined

  const lines = stack.split('\n')
  const sanitizedLines = [
    `${name}: ${message}`,
    ...lines.slice(1).map((line) =>
      redactSensitiveText(line.replace(urlPattern, sanitizeStackUrl), {
        redactUrls: false
      })
    )
  ]

  return truncate(sanitizedLines.join('\n'), MAX_STACK_LENGTH)
}

export function normalizeExceptionForTelemetry(
  error: unknown,
  fallbackMessage = DEFAULT_EXCEPTION_MESSAGE
): NormalizedException {
  if (error instanceof Error) {
    const name = sanitizeExceptionName(error.name)
    const message = sanitizeExceptionMessage(error.message, fallbackMessage)
    const stack = sanitizeErrorStack(error.stack, name, message)
    const digest = readDigest(error)

    return {
      name,
      message,
      ...(digest ? { digest } : {}),
      ...(stack ? { stack } : {})
    }
  }

  if (isRecord(error)) {
    const name = sanitizeExceptionName(
      readRecordString(error, 'name') ?? readRecordString(error, 'type')
    )
    const message = sanitizeExceptionMessage(
      readRecordString(error, 'message') ??
        readRecordString(error, 'error') ??
        readRecordString(error, 'reason'),
      fallbackMessage
    )
    const stack = sanitizeErrorStack(readRecordString(error, 'stack') ?? undefined, name, message)
    const digest = readDigest(error)

    return {
      name,
      message,
      ...(digest ? { digest } : {}),
      ...(stack ? { stack } : {})
    }
  }

  return {
    name: DEFAULT_EXCEPTION_NAME,
    message: sanitizeExceptionMessage(error, fallbackMessage)
  }
}

function getRelease() {
  return (
    readString(process.env.NEXT_PUBLIC_SENTRY_RELEASE) ??
    readString(process.env.SENTRY_RELEASE) ??
    readString(process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA) ??
    readString(process.env.VERCEL_GIT_COMMIT_SHA) ??
    null
  )
}

function getCommitSha() {
  return (
    readString(process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA) ??
    readString(process.env.VERCEL_GIT_COMMIT_SHA) ??
    readString(process.env.NEXT_PUBLIC_COMMIT_SHA) ??
    readString(process.env.COMMIT_SHA) ??
    null
  )
}

function normalizeQueryKeys(keys: string[] | null | undefined) {
  const safeKeys = Array.from(new Set(keys ?? []))
    .map((key) => key.trim())
    .filter(Boolean)
    .filter((key) => !sensitiveQueryKeys.has(key.toLowerCase()))
    .sort()

  return safeKeys.length > 0 ? safeKeys : undefined
}

function buildDedupeKey(args: {
  name: string
  message: string
  path: string
  source: string
  activationSurface: ActivationSurface
  activationStage?: string
  digest?: string
}) {
  return [
    args.name,
    args.message,
    args.path,
    args.source,
    args.activationSurface,
    args.activationStage ?? '',
    args.digest ?? ''
  ].join('|')
}

export function buildExceptionTelemetryPayload(
  error: unknown,
  context: ExceptionTelemetryContext
): ExceptionTelemetryPayload {
  const normalized = normalizeExceptionForTelemetry(error)
  const browserContext = getBrowserExceptionTelemetryContext()
  const safeQueryKeys = normalizeQueryKeys(
    context.safeQueryKeys ??
      getSafeQueryKeyNames(context.path ?? undefined) ??
      browserContext.safeQueryKeys
  )
  const path = sanitizePath(context.path ?? browserContext.path)
  const route = sanitizePath(
    context.route ?? (context.path ? path : browserContext.route) ?? path,
    path
  )
  const source = sanitizeTelemetryLabel(context.source, 'unknown')
  const boundary = context.boundary
    ? sanitizeTelemetryLabel(context.boundary, source)
    : undefined
  const activationSurface =
    context.activationSurface ?? inferActivationSurface(path, safeQueryKeys)
  const activationStage = context.activationStage
    ? sanitizeTelemetryLabel(context.activationStage, 'unknown')
    : undefined
  const digest = context.digest ?? normalized.digest
  const release = readString(context.release) ?? getRelease()
  const commitSha = readString(context.commitSha) ?? getCommitSha()
  const severity = context.severity ?? 'error'
  const dedupeKey = buildDedupeKey({
    name: normalized.name,
    message: normalized.message,
    path,
    source,
    activationSurface,
    ...(activationStage ? { activationStage } : {}),
    ...(digest ? { digest } : {})
  })

  return {
    '$exception_type': normalized.name,
    '$exception_message': normalized.message,
    '$exception_level': severity,
    ...(normalized.stack
      ? {
          '$exception_stack_trace_raw': normalized.stack,
          exception_stack: normalized.stack,
          error_stack: normalized.stack,
          stack: normalized.stack
        }
      : {}),
    exception_type: normalized.name,
    exception_name: normalized.name,
    exception_message: normalized.message,
    error_type: normalized.name,
    error_name: normalized.name,
    error_message: normalized.message,
    source,
    severity,
    path,
    route,
    activation_surface: activationSurface,
    dedupe_key: dedupeKey,
    ...(boundary ? { boundary } : {}),
    ...(digest ? { digest } : {}),
    ...(context.userId ? { user_id: context.userId } : {}),
    ...(context.anonymousId ? { anonymous_id: context.anonymousId } : {}),
    ...(context.sessionId ? { session_id: context.sessionId } : {}),
    ...(release ? { release } : {}),
    ...(commitSha ? { commit_sha: commitSha } : {}),
    ...(typeof context.firstSession === 'boolean'
      ? { first_session: context.firstSession }
      : {}),
    ...(activationStage ? { activation_stage: activationStage } : {}),
    ...(safeQueryKeys ? { query_keys: safeQueryKeys } : {})
  }
}

export function createTelemetryError(
  error: unknown,
  payload: ExceptionTelemetryPayload
) {
  const normalized = normalizeExceptionForTelemetry(error)
  const telemetryError = new Error(payload.exception_message)

  telemetryError.name = payload.exception_name

  if (normalized.stack) {
    telemetryError.stack = normalized.stack
  }

  if (payload.digest) {
    return Object.assign(telemetryError, { digest: payload.digest })
  }

  return telemetryError
}
